import { useState, useMemo } from 'react';
import { Card } from '../../../layout/Card.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { useAuth } from '../../../shared/useAuth.js';
import { formatIQD, formatDateTime } from '../../../shared/format.js';
import { useTransfers } from '../hooks/useTransfers.js';
import { useCashboxes } from '../hooks/useCashboxes.js';
import { TransferForm } from './TransferForm.jsx';
import { ViewAllToggle, ViewAllNotice } from '../../../shared/ViewAllToggle.jsx';
import { listParams } from '../../../shared/viewAll.js';

// عمود «النوع» (referenceType) مُزال من شاشة القاصات بطلب العمل.
// الحقل نفسه باقٍ في القاعدة وفي كل منطق الخادم (إيداعات/عهد الشفتات تستعمل
// referenceType: 'shift'، والتقارير تجمّع عليه) — الإزالة هنا عرضية فقط.

/** اسم منفّذ العملية: لقطة الاسم أولاً، ثم المستخدم المُلحَق. */
function actorName(t) {
  if (!t) return '—';
  const u = t.createdById;
  const name = t.createdByName
    || (u && typeof u === 'object' ? (u.fullName || u.username) : '')
    || t.createdBy?.fullName
    || t.createdBy?.username;
  return name || '—';
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function startOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

export function TransfersList() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canTransfer = hasPermission('cashbox.transfer');
  const { data: cashboxes = [] } = useCashboxes();

  const [from, setFrom] = useState(startOfMonthISO());
  const [to, setTo] = useState(todayISO());
  const [cashboxId, setCashboxId] = useState('');
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [details, setDetails] = useState(null);

  const params = useMemo(
    () => ({ from, to, cashboxId: cashboxId || undefined, ...listParams({ all: showAll, page, limit: 20 }) }),
    [from, to, cashboxId, page, showAll]
  );

  const { data, isLoading, error } = useTransfers(params);
  const transfers = data?.transfers || data?.items || data?.data || [];
  const pagination = data?.pagination || data?.meta || {
    total: data?.total ?? transfers.length,
    page: data?.page ?? page,
    // الخادم يسمّيه pages — بدونه كان زر «التالي» معطّلاً دائماً.
    totalPages: data?.pages ?? 1
  };

  const cbMap = useMemo(() => {
    const map = {};
    for (const c of cashboxes) map[c._id || c.id] = c;
    return map;
  }, [cashboxes]);

  function nameOf(idOrObj) {
    if (!idOrObj) return '—';
    if (typeof idOrObj === 'object') return idOrObj.name || cbMap[idOrObj._id]?.name || '—';
    return cbMap[idOrObj]?.name || '—';
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[15px] font-bold">التحويلات</div>
          <div className="text-xs text-ink-3 mt-0.5">سجل التحويلات بين القاصات</div>
        </div>
        <div className="flex items-center gap-2">
          <ViewAllToggle
            all={showAll}
            onChange={(v) => { setShowAll(v); setPage(1); }}
            shown={transfers.length}
            total={pagination.total}
          />
          {canTransfer ? (
            <button type="button" className="btn btn-primary" onClick={() => setFormOpen(true)}>
              <Icon name="plus" size={14} /> تحويل جديد
            </button>
          ) : null}
        </div>
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-2.5">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold text-ink-2 mb-1">من تاريخ</label>
            <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold text-ink-2 mb-1">إلى تاريخ</label>
            <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-semibold text-ink-2 mb-1">القاصة</label>
            <select className="input" value={cashboxId} onChange={(e) => setCashboxId(e.target.value)}>
              <option value="">جميع القاصات</option>
              {cashboxes.map((c) => (
                <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {error ? (
        <Card><div className="text-red text-center py-4">تعذّر تحميل التحويلات</div></Card>
      ) : isLoading ? (
        <Card><div className="text-ink-3 text-center py-8">جاري التحميل...</div></Card>
      ) : transfers.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10">
            لا توجد تحويلات في هذه الفترة
          </div>
        </Card>
      ) : (
        <Card>
          <table className="w-full text-[13px]">
            <thead className="text-ink-2 text-[12px]">
              <tr className="border-b border-line">
                <th className="p-2.5 text-start">التاريخ</th>
                <th className="p-2.5 text-start">من</th>
                <th className="p-2.5 text-start">إلى</th>
                <th className="p-2.5 text-start">المبلغ</th>
                <th className="p-2.5 text-start">المنفّذ</th>
                <th className="p-2.5 text-start">ملاحظة</th>
                <th className="p-2.5 text-end" />
              </tr>
            </thead>
            <tbody>
              {transfers.map((t) => (
                <tr key={t._id || t.id} className="border-b border-line">
                  <td className="p-2.5 text-ink-2">{formatDateTime(t.createdAt || t.date)}</td>
                  <td className="p-2.5 font-bold">{nameOf(t.fromCashboxId || t.from)}</td>
                  <td className="p-2.5 font-bold">{nameOf(t.toCashboxId || t.to)}</td>
                  <td className="p-2.5 font-bold" style={{ color: '#C2570D' }}>
                    {formatIQD(t.amount || 0)}
                  </td>
                  <td className="p-2.5 text-ink-2 font-semibold">{actorName(t)}</td>
                  <td className="p-2.5 text-ink-3 truncate" style={{ maxWidth: 220 }}>
                    {t.note || '—'}
                  </td>
                  <td className="p-2.5 text-end">
                    <button
                      type="button"
                      className="btn"
                      style={{ padding: '4px 8px' }}
                      onClick={() => setDetails(t)}
                      title="تفاصيل العملية"
                    >
                      تفاصيل
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {showAll ? (
            <ViewAllNotice all={showAll} shown={transfers.length} total={pagination.total} />
          ) : pagination.totalPages > 1 ? (
            <div className="flex items-center justify-between pt-3 border-t border-line mt-3">
              <div className="text-xs text-ink-3">
                صفحة {pagination.page || page} من {pagination.totalPages}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  className="btn"
                  style={{ padding: '6px 10px' }}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  السابق
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ padding: '6px 10px' }}
                  disabled={page >= (pagination.totalPages || 1)}
                  onClick={() => setPage((p) => p + 1)}
                >
                  التالي
                </button>
              </div>
            </div>
          ) : null}
        </Card>
      )}

      <TransferForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        cashboxes={cashboxes}
      />

      {details ? (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgba(0,0,0,.45)' }}>
          <div className="card w-[440px] max-w-full" onClick={(e) => e.stopPropagation()}>
            <div className="card-h">
              <h3 className="text-[15px] font-bold flex-1 m-0">تفاصيل العملية</h3>
              <button type="button" className="btn btn-ghost" onClick={() => setDetails(null)}>
                <Icon name="x" size={14} />
              </button>
            </div>
            <div className="card-b">
              <dl className="grid grid-cols-[110px_1fr] gap-y-2 gap-x-3 text-[13px] m-0">
                <dt className="text-ink-3">التاريخ</dt>
                <dd className="m-0 text-ink-2">{formatDateTime(details.createdAt || details.date)}</dd>
                <dt className="text-ink-3">من قاصة</dt>
                <dd className="m-0 font-bold">{nameOf(details.fromCashboxId || details.from)}</dd>
                <dt className="text-ink-3">إلى قاصة</dt>
                <dd className="m-0 font-bold">{nameOf(details.toCashboxId || details.to)}</dd>
                <dt className="text-ink-3">المبلغ</dt>
                <dd className="m-0 font-bold" style={{ color: '#C2570D' }}>{formatIQD(details.amount || 0)}</dd>
                <dt className="text-ink-3">المنفّذ</dt>
                <dd className="m-0 font-bold">{actorName(details)}</dd>
                <dt className="text-ink-3">ملاحظة</dt>
                <dd className="m-0 text-ink-2">{details.note || '—'}</dd>
              </dl>
            </div>
            <div className="card-h border-b-0 border-t flex justify-end">
              <button type="button" className="btn" onClick={() => setDetails(null)}>إغلاق</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
